import { booleanIntersects } from '@turf/boolean-intersects';
import { addHours, parseISO } from 'date-fns';
import type { Feature, Geometry, Polygon } from 'geojson';
import proj4 from 'proj4';
import { useEffect, useState } from 'react';
import { reproject } from 'reproject';

import { CongestionTaxInfo } from '../CongestionTax/CongestionTaxInfo';
import type { CongestionTaxOverview } from '../CongestionTax/CongestionTaxInfo.types';
import { LoadingSpinner } from '../LoadingSpinner';
import { PublicTransportEventsBox, type PublicTransportTypesWrapper } from '../PublicTransportEvents/PublicTransportEventsBox';
import { useTimeoutState } from '../Util/useTimeoutState';
import { type Area, type Situation } from './Situation.types';
import { SituationListFilter } from './SituationFilter';
import { SituationList } from './SituationList';

export interface ISituationPageProps {
    areas: Area[];
    situations: Situation[];
    currentRegion: string;
    congestionTaxOverview: CongestionTaxOverview;
    slTypes: PublicTransportTypesWrapper[];
    region: string;
}

export interface SituationFilterObject {
    searchText: string;
    area: string;
    checkedFilters: string[];
    dateFrom: string;
    dateTo: string;
}

const defaultFilterObject: SituationFilterObject = {
    searchText: '',
    area: '',
    checkedFilters: [
        'road',
        'accidents-and-traffic-messages',
        'road-works',
        'events',
        'ferry',
        'bicycle',
        'public-transport',
        'bus',
        'tram',
        'pt-train',
        'boat',
        'pt-other',
        'train',
    ],
    dateFrom: '',
    dateTo: '',
};

const SituationPage = ({ areas, situations, currentRegion, congestionTaxOverview, slTypes, region }: ISituationPageProps) => {
    const [currentFilter, setCurrentFilter] = useState<SituationFilterObject>(defaultFilterObject);
    const [loading, setLoading] = useTimeoutState(false);
    const [initializing, setInitializing] = useState(true);
    const [situationsAreOutdated, setSituationsAreOutdated] = useState(false);

    useEffect(() => {
        const filterState = history.state as SituationFilterObject;
        if (filterState && !!filterState.checkedFilters) {
            setCurrentFilter(filterState);
        }

        setInitializing(false);
    }, []);

    useEffect(() => {
        function compareSituations(arr1: Situation[], arr2: Situation[]): boolean {
            if (arr1.length !== arr2.length) return false;

            for (let i = 0; i < arr1.length; i++) {
                if (arr1[i].id !== arr2[i].id) return false;
            }
            return true;
        }

        async function getSituations() {
            await fetch(`/api/region/${region}/traffic-situations`)
                .then((response) => response.json())
                .then((data: Situation[]) => {
                    setSituationsAreOutdated(!compareSituations(situations, data));
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                });
        }
        let interval: NodeJS.Timeout;
        if (!situationsAreOutdated) {
            interval = setInterval(() => {
                getSituations();
            }, 30000);
        }

        return () => clearInterval(interval);
    }, [situationsAreOutdated]);

    const handleUpdate = (filter?: SituationFilterObject) => {
        setLoading(true);
        const newFilter = filter ?? defaultFilterObject;
        history.replaceState(newFilter, 'FilterState');
        setLoading(false, 300);
        setCurrentFilter(newFilter);
    };

    const visibleSituations = currentFilter ? getFilteredSituations(situations, currentFilter, region) : [];

    return (
        <div className="row mb-3">
            <div className="col-12 col-md-8 position-relative">
                <div className="row">
                    <SituationListFilter
                        areas={areas}
                        filterState={currentFilter}
                        onUpdate={handleUpdate}
                        onReset={() => handleUpdate()}
                        loading={initializing}
                        changedFilterCount={changedFilterCount(currentFilter)}
                        region={region}
                        className="mb-4"
                    />
                    <div className="col-12" aria-live="polite">
                        {situationsAreOutdated && (
                            <div className="block p-3 d-flex align-items-center justify-content-between">
                                Det finns uppdaterad trafikinformation
                                <button type="button" className="btn btn-link p-0 flex-shrink-0" onClick={() => window.location.reload()}>
                                    Ladda om sidan
                                    <span className="icon-arrows-cw" aria-hidden="true"></span>
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="col-12 mt-4" aria-live="polite" aria-busy={initializing || (loading && visibleSituations.length === 0)}>
                        {initializing || (loading && visibleSituations.length === 0) ? (
                            <LoadingSpinner />
                        ) : visibleSituations.length === 0 ? (
                            <div className="no-result">
                                <p>Det finns inga resultat att visa</p>
                            </div>
                        ) : (
                            <>
                                <h2 className="visually-hidden">Filtrerade situationer</h2>
                                <SituationList items={visibleSituations} />
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div className="col-12 col-md-4 situation-page__side-panel list-page-margin pre-line">
                {congestionTaxOverview ? (
                    <div className="block">
                        <h2 className="h5 yellow-border-bottom">Aktuell trängselskatt</h2>
                        <CongestionTaxInfo overview={congestionTaxOverview} region={currentRegion} />
                    </div>
                ) : null}
                {slTypes?.length > 0 ? (
                    <div className="block mt-4">
                        <h2 className="h5 yellow-border-bottom">Kollektivtrafik</h2>
                        <PublicTransportEventsBox events={slTypes} />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

function getFilteredSituations(situations: Situation[], selectedFilters: SituationFilterObject, region: string): Situation[] {
    const searchText = selectedFilters.searchText;
    const filterArea = selectedFilters.area && JSON.parse(selectedFilters.area);
    const fromDate = parseDate(selectedFilters.dateFrom);
    let toDate = parseDate(selectedFilters.dateTo);
    if (!fromDate && !toDate) {
        toDate = addHours(new Date(), 18);
    }

    const filteredSituations = situations.filter((situation: Situation) => {
        return (
            (!searchText || filterByText(situation, selectedFilters.searchText)) &&
            (!filterArea || filterByArea(situation, filterArea)) &&
            filterByDate(situation, fromDate, toDate) &&
            filterByBooleanFilters(situation, selectedFilters.checkedFilters, region)
        );
    });

    return filteredSituations;
}

function filterByText(situation: Situation, searchText: string): boolean {
    const searchtext = searchText.toLowerCase();
    const messageCode = situation.messageCode ?? '';
    const messageType = situation.messageType ?? '';
    const endTime = situation.endTime ? situation.endTime : '';
    const startTime = situation.startTime ? situation.startTime : '';

    if (
        situation.title?.toLowerCase().indexOf(searchtext) > -1 ||
        endTime?.toString().indexOf(searchtext) > -1 ||
        startTime?.toString().indexOf(searchtext) > -1 ||
        messageCode.toLowerCase().indexOf(searchtext) > -1 ||
        messageType.toLowerCase().indexOf(searchtext) > -1
    ) {
        return true;
    }

    return false;
}

function filterByArea(situation: Situation, area: Feature): boolean {
    if (['Kollektivtrafik', 'Tågtrafik'].includes(situation.trafficType)) {
        return false;
    }

    let comparer = situation.sweRef99Area as Geometry;

    comparer ??= reproject(situation.wgs84Position, proj4.WGS84, '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs', proj4);

    if (comparer === undefined || comparer === null) {
        return false;
    }

    const itemArea = { geometry: comparer, type: 'Feature', properties: {} } as Feature<Polygon>;
    return booleanIntersects(itemArea, area);
}

function parseDate(date?: string): Date {
    if (date == null || !date) {
        return null;
    }

    const parsedDate = parseISO(date);
    if (parsedDate === null) {
        return null;
    }
    parsedDate.setHours(0, 0, 0, 0);
    return parsedDate;
}

function filterByDate(situation: Situation, startDate?: Date, endDate?: Date): boolean {
    const itemStartDate = parseDate(situation.startTime);
    const itemEndDate = parseDate(situation.endTime);
    return (!startDate || !itemEndDate || startDate <= itemEndDate) && (!endDate || endDate >= itemStartDate);
}

const publicTransportSubFilters: Record<string, string> = {
    Buss: 'bus',
    Spårvagn: 'tram',
    Tåg: 'pt-train',
    Båt: 'boat',
};

function filterByBooleanFilters(situation: Situation, checkedFilters: string[], region: string): boolean {
    if (checkedFilters.length === 0) {
        return false;
    }

    const messageCode = situation.messageCode ?? '';
    const messageType = situation.messageType ?? '';

    if (checkedFilters.includes('road') && situation.trafficType === 'Vägtrafik') {
        let include =
            checkedFilters.includes('accidents-and-traffic-messages') &&
            messageType !== 'Vägarbete' &&
            !['Färja', 'Stort evenemang'].includes(messageCode);
        include = include || (checkedFilters.includes('road-works') && messageType === 'Vägarbete');
        include = include || (checkedFilters.includes('events') && messageCode.toLowerCase().includes('evenemang'));
        include = include || (checkedFilters.includes('ferry') && messageCode === 'Färja');

        return include;
    }

    if (checkedFilters.includes('bicycle') && situation.trafficType === 'Cykeltrafik') {
        return true;
    }

    if (checkedFilters.includes('public-transport') && situation.trafficType === 'Kollektivtrafik') {
        if (region !== 'gbg') {
            return true;
        }
        const subFilterId = publicTransportSubFilters[messageType] ?? 'pt-other';
        return checkedFilters.includes(subFilterId);
    }

    if (checkedFilters.includes('train') && situation.trafficType === 'Tågtrafik') {
        return true;
    }

    return false;
}

function changedFilterCount(filter: SituationFilterObject): number {
    let filterCount = 0;
    Object.entries(filter).forEach(([key, value]) => {
        if (key === 'checkedFilters') {
            const defaultArray = defaultFilterObject[key];
            const changed = [...value.filter((v) => !defaultArray.includes(v)), ...defaultArray.filter((v) => !value.includes(v))];
            filterCount += changed.length;
        } else if (key != 'searchText') {
            if (value !== defaultFilterObject[key]) {
                filterCount++;
            }
        }
    });
    return filterCount;
}

export { SituationPage };
